import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";
import { toast } from "react-toastify";
import DatePicker from "react-datepicker";
import { Calendar, ChevronDown, X } from "lucide-react";
import { createClient, updateClientById, getUsers } from "../config/url";
import { getConfigWithToken } from "../config/config";
import classes from "./AddClientDrawer.module.css";
import "react-datepicker/dist/react-datepicker.css";

const NAME_PATTERN = /^[A-Za-z][A-Za-z\s'-]*$/;
const CLIENT_ID_PATTERN = /^[A-Za-z0-9-]+$/;
const PAYER_ID_PATTERN = /^[A-Za-z0-9-]+$/;

const emptyForm = {
  firstName: "",
  lastName: "",
  dob: "",
  clientid: "",
  phone_number: "",
  payer_member_id: "",
  user_id: "",
  status: "Active",
};

const formatUsPhone = (value) => {
  const digits = (value || "").replace(/\D/g, "").slice(0, 10);
  const a = digits.slice(0, 3);
  const b = digits.slice(3, 6);
  const c = digits.slice(6, 10);
  if (digits.length <= 3) return a ? `(${a}` : "";
  if (digits.length <= 6) return `(${a}) ${b}`;
  return `(${a}) ${b}-${c}`;
};

const formatDobInput = (value) => {
  const digits = (value || "").replace(/\D/g, "").slice(0, 8);
  const mm = digits.slice(0, 2);
  const dd = digits.slice(2, 4);
  const yyyy = digits.slice(4, 8);
  if (digits.length <= 2) return mm;
  if (digits.length <= 4) return `${mm}/${dd}`;
  return `${mm}/${dd}/${yyyy}`;
};

const displayToIso = (display) => {
  if (!/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(display || "")) return "";
  const [mm, dd, yyyy] = display.split("/").map((p) => p.padStart(2, "0"));
  return `${yyyy}-${mm}-${dd}`;
};

const isoToDisplay = (value) => {
  if (!value) return "";
  const raw = String(value);
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) {
    const [y, m, d] = raw.slice(0, 10).split("-");
    return `${m}/${d}/${y}`;
  }
  const date = new Date(raw.includes("T") ? raw : `${raw}T00:00:00`);
  if (Number.isNaN(date.getTime())) return "";
  return formatDateToDisplay(date);
};

const parseDisplayDob = (display) => {
  const iso = displayToIso(display);
  if (!iso) return null;
  const [year, month, day] = iso.split("-").map(Number);
  const date = new Date(year, month - 1, day);
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return null;
  }
  return date;
};

const formatDateToDisplay = (date) => {
  if (!date || Number.isNaN(date.getTime())) return "";
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  const yyyy = date.getFullYear();
  return `${mm}/${dd}/${yyyy}`;
};

const isActiveStatus = (status) => {
  if (status == null || status === "") return false;
  if (typeof status === "boolean") return status;
  if (typeof status === "number") return status === 1;
  const normalized = String(status).trim().toLowerCase();
  return ["active", "true", "1"].includes(normalized);
};

const clientToForm = (client) => {
  if (!client) return { ...emptyForm };
  return {
    firstName: client.firstName || "",
    lastName: client.lastName || "",
    dob: isoToDisplay(client.dob),
    clientid: client.clientid || "",
    phone_number: client.phone_number || "",
    payer_member_id: client.payer_member_id || "",
    user_id: client.user_id || "",
    status: isActiveStatus(client.status) ? "Active" : "Inactive",
  };
};

/**
 * Shared Add/Edit Client drawer.
 * mode: "create" | "edit"
 */
const ClientFormDrawer = ({
  open,
  onClose,
  onCreated,
  onSaved,
  mode = "create",
  client = null,
}) => {
  const isEdit = mode === "edit";
  const navigate = useNavigate();
  const currentUser = useSelector((state) => state?.user);
  const token = currentUser?.user?.token || localStorage.getItem("user_id");
  const organizationId =
    currentUser?.user?.organizationId ||
    localStorage.getItem("organizationId") ||
    currentUser?.user?.userId ||
    "";

  const [form, setForm] = useState(emptyForm);
  const [initialSnapshot, setInitialSnapshot] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [providers, setProviders] = useState([]);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [serverError, setServerError] = useState("");
  const [nameDobMatches, setNameDobMatches] = useState([]);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [dobOpen, setDobOpen] = useState(false);
  const [dobMenuStyle, setDobMenuStyle] = useState(null);
  const dirtyRef = useRef(false);
  const dobWrapRef = useRef(null);
  const dobInputRef = useRef(null);

  const requestConfig = useMemo(
    () => ({
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        ...(token ? getConfigWithToken(token).headers : {}),
        ...(organizationId ? { "x-organization-id": organizationId } : {}),
      },
    }),
    [organizationId, token]
  );

  const isDirty = useMemo(
    () =>
      Object.keys(form).some(
        (key) => String(form[key] || "").trim() !== String(initialSnapshot[key] || "").trim()
      ),
    [form, initialSnapshot]
  );

  useEffect(() => {
    dirtyRef.current = isDirty;
  }, [isDirty]);

  useEffect(() => {
    if (!open) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKeyDown = (event) => {
      if (event.key === "Escape") requestClose();
    };
    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const nextForm = isEdit ? clientToForm(client) : { ...emptyForm };
    setForm(nextForm);
    setInitialSnapshot(nextForm);
    setErrors({});
    setServerError("");
    setNameDobMatches([]);
    setConfirmDiscard(false);
    setDobOpen(false);
    dirtyRef.current = false;

    const loadProviders = async () => {
      try {
        const response = await axios.get(getUsers, requestConfig);
        const rows = response?.data?.data || [];
        const list = (Array.isArray(rows) ? rows : [])
          .filter((u) => u && u._id && u.isactive !== false && !u.isDeleted)
          .filter((u) => {
            if (!organizationId || !u.adminId) return true;
            return String(u.adminId) === String(organizationId);
          })
          .map((u) => ({
            id: u._id,
            name:
              [u.firstname, u.lastname].filter(Boolean).join(" ").trim() ||
              u.userId ||
              u.email ||
              "Provider",
          }))
          .sort((a, b) => a.name.localeCompare(b.name));
        setProviders(list);
      } catch {
        setProviders([]);
      }
    };

    loadProviders();
    // Only re-initialize when the drawer opens or the edited client identity changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, organizationId, requestConfig, isEdit, client?.id]);

  useEffect(() => {
    if (!dobOpen) return undefined;

    const updatePosition = () => {
      const anchor = dobInputRef.current || dobWrapRef.current;
      if (!anchor) return;
      const rect = anchor.getBoundingClientRect();
      const width = Math.max(rect.width, 280);
      let left = rect.left;
      if (left + width > window.innerWidth - 8) {
        left = Math.max(8, window.innerWidth - width - 8);
      }
      setDobMenuStyle({
        left,
        top: rect.bottom + 6,
        width,
      });
    };

    updatePosition();

    const onPointerDown = (event) => {
      const menu = document.getElementById("client-form-dob-calendar");
      if (
        dobWrapRef.current?.contains(event.target) ||
        menu?.contains(event.target)
      ) {
        return;
      }
      setDobOpen(false);
    };

    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    document.addEventListener("mousedown", onPointerDown);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
      document.removeEventListener("mousedown", onPointerDown);
    };
  }, [dobOpen]);

  const setField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
    setServerError("");
    setNameDobMatches([]);
  };

  const validateClient = () => {
    const next = {};
    const firstName = form.firstName.trim();
    const lastName = form.lastName.trim();
    const clientid = form.clientid.trim();
    const phoneDigits = form.phone_number.replace(/\D/g, "");
    const payer = form.payer_member_id.trim();

    if (!firstName) next.firstName = "First name is required.";
    else if (!NAME_PATTERN.test(firstName)) {
      next.firstName =
        "First name may only include letters, hyphens, apostrophes, and spaces.";
    }

    if (!lastName) next.lastName = "Last name is required.";
    else if (!NAME_PATTERN.test(lastName)) {
      next.lastName =
        "Last name may only include letters, hyphens, apostrophes, and spaces.";
    }

    if (!form.dob.trim()) next.dob = "Date of birth is required.";
    else {
      const iso = displayToIso(form.dob.trim());
      if (!iso) next.dob = "Enter a valid date of birth (MM/DD/YYYY).";
      else {
        const [y, m, d] = iso.split("-").map(Number);
        const date = new Date(y, m - 1, d);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (
          date.getFullYear() !== y ||
          date.getMonth() !== m - 1 ||
          date.getDate() !== d
        ) {
          next.dob = "Enter a valid date of birth (MM/DD/YYYY).";
        } else if (date > today) {
          next.dob = "Date of birth cannot be a future date.";
        }
      }
    }

    if (!clientid) next.clientid = "Client ID is required.";
    else if (!CLIENT_ID_PATTERN.test(clientid)) {
      next.clientid = "Client ID may only include letters, numbers, and hyphens.";
    }

    if (!phoneDigits) next.phone_number = "Phone number is required.";
    else if (phoneDigits.length !== 10) {
      next.phone_number = "Enter a valid U.S. phone number.";
    }

    if (payer && !PAYER_ID_PATTERN.test(payer)) {
      next.payer_member_id =
        "Payer Member ID may only include letters, numbers, and hyphens.";
    }

    if (!form.status) next.status = "Status is required.";

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const requestClose = () => {
    if (dirtyRef.current || isDirty) {
      setConfirmDiscard(true);
      return;
    }
    onClose?.();
  };

  const confirmClose = () => {
    setConfirmDiscard(false);
    onClose?.();
  };

  const submitClient = async ({ acknowledgeNameDobWarning = false } = {}) => {
    if (!validateClient()) return;

    setSubmitLoading(true);
    setServerError("");

    const payload = {
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      dob: displayToIso(form.dob.trim()),
      clientid: form.clientid.trim(),
      phone_number: form.phone_number.trim(),
      payer_member_id: form.payer_member_id.trim() || null,
      user_id: form.user_id || null,
      status: form.status === "Active" ? "active" : "inactive",
      organization_id: organizationId || null,
      ...(isEdit ? {} : { acknowledgeNameDobWarning }),
    };

    try {
      if (isEdit) {
        if (!client?.id) throw new Error("Missing client id");
        const response = await axios.put(
          updateClientById(client.id),
          payload,
          requestConfig
        );
        toast.success(response?.data?.message || "Client updated successfully");
        onSaved?.(response?.data?.data || payload);
        onClose?.();
      } else {
        const response = await axios.post(createClient, payload, requestConfig);
        toast.success(response?.data?.message || "Client added successfully");
        onCreated?.(response?.data?.data);
        onClose?.();
      }
    } catch (err) {
      const data = err?.response?.data;
      if (!isEdit && data?.code === "NAME_DOB_MATCH") {
        setNameDobMatches(data?.data?.matches || []);
        setServerError(data?.message || "A possible matching client was found.");
      } else if (data?.errors) {
        setErrors((prev) => ({ ...prev, ...data.errors }));
        setServerError(data.message || "Please fix the highlighted fields.");
      } else {
        setServerError(
          data?.message ||
            err?.message ||
            (isEdit
              ? "Unable to update client. Please try again."
              : "Unable to add client. Please try again.")
        );
      }
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    await submitClient();
  };

  const openExistingClient = (id) => {
    navigate(`/clients/${id}`);
  };

  if (!open) return null;

  return createPortal(
    <div className={classes.root} role="presentation">
      <button
        aria-label={isEdit ? "Close edit client drawer" : "Close add client drawer"}
        className={classes.overlay}
        onClick={requestClose}
        type="button"
      />

      <aside
        aria-labelledby="client-form-title"
        aria-modal="true"
        className={classes.drawer}
        role="dialog"
      >
        <div className={classes.header}>
          <div>
            <h2 className={classes.title} id="client-form-title">
              {isEdit ? "Edit Client" : "Add Client"}
            </h2>
            <p className={classes.subtitle}>Fill in the client&apos;s information below.</p>
          </div>
          <button
            aria-label="Close"
            className={classes.closeButton}
            onClick={requestClose}
            type="button"
          >
            <X size={16} strokeWidth={2} />
          </button>
        </div>

        <form className={classes.form} onSubmit={handleSubmit} noValidate>
          <div className={classes.body}>
            <div className={classes.gridTwo}>
              <label className={classes.field}>
                <span className={classes.label}>
                  First Name <span className={classes.required}>*</span>
                </span>
                <input
                  className={`${classes.input} ${errors.firstName ? classes.inputError : ""}`}
                  onChange={(e) => setField("firstName", e.target.value)}
                  placeholder="First"
                  type="text"
                  value={form.firstName}
                />
                {errors.firstName ? (
                  <span className={classes.errorText}>{errors.firstName}</span>
                ) : null}
              </label>

              <label className={classes.field}>
                <span className={classes.label}>
                  Last Name <span className={classes.required}>*</span>
                </span>
                <input
                  className={`${classes.input} ${errors.lastName ? classes.inputError : ""}`}
                  onChange={(e) => setField("lastName", e.target.value)}
                  placeholder="Last"
                  type="text"
                  value={form.lastName}
                />
                {errors.lastName ? (
                  <span className={classes.errorText}>{errors.lastName}</span>
                ) : null}
              </label>
            </div>

            <div className={classes.gridTwo}>
              <label className={classes.field}>
                <span className={classes.label}>
                  Date of Birth <span className={classes.required}>*</span>
                </span>
                <div className={classes.dateWrap} ref={dobWrapRef}>
                  <Calendar
                    aria-hidden="true"
                    className={classes.dateIcon}
                    size={15}
                    strokeWidth={2}
                  />
                  <input
                    className={`${classes.input} ${classes.dateInput} ${
                      errors.dob ? classes.inputError : ""
                    }`}
                    inputMode="numeric"
                    maxLength={10}
                    onChange={(e) => setField("dob", formatDobInput(e.target.value))}
                    onClick={() => setDobOpen(true)}
                    onFocus={() => setDobOpen(true)}
                    placeholder="MM/DD/YYYY"
                    ref={dobInputRef}
                    type="text"
                    value={form.dob}
                  />
                </div>
                {errors.dob ? <span className={classes.errorText}>{errors.dob}</span> : null}
              </label>

              <label className={classes.field}>
                <span className={classes.label}>
                  Client ID <span className={classes.required}>*</span>
                </span>
                <input
                  className={`${classes.input} ${errors.clientid ? classes.inputError : ""}`}
                  onChange={(e) => setField("clientid", e.target.value)}
                  type="text"
                  value={form.clientid}
                />
                {errors.clientid ? (
                  <span className={classes.errorText}>{errors.clientid}</span>
                ) : null}
              </label>
            </div>

            <div className={classes.gridTwo}>
              <label className={classes.field}>
                <span className={classes.label}>
                  Phone Number <span className={classes.required}>*</span>
                </span>
                <input
                  className={`${classes.input} ${
                    errors.phone_number ? classes.inputError : ""
                  }`}
                  onChange={(e) => setField("phone_number", formatUsPhone(e.target.value))}
                  type="text"
                  value={form.phone_number}
                />
                {errors.phone_number ? (
                  <span className={classes.errorText}>{errors.phone_number}</span>
                ) : null}
              </label>

              <label className={classes.field}>
                <span className={classes.label}>Payer Member ID</span>
                <input
                  className={`${classes.input} ${
                    errors.payer_member_id ? classes.inputError : ""
                  }`}
                  onChange={(e) => setField("payer_member_id", e.target.value)}
                  placeholder="Member ID"
                  type="text"
                  value={form.payer_member_id}
                />
                {errors.payer_member_id ? (
                  <span className={classes.errorText}>{errors.payer_member_id}</span>
                ) : null}
              </label>
            </div>

            <div className={classes.gridTwo}>
              <label className={classes.field}>
                <span className={classes.label}>Assigned Provider</span>
                <div className={classes.selectWrap}>
                  <select
                    className={`${classes.select} ${errors.user_id ? classes.inputError : ""}`}
                    onChange={(e) => setField("user_id", e.target.value)}
                    value={form.user_id}
                  >
                    <option value="">Select provider</option>
                    {providers.map((provider) => (
                      <option key={provider.id} value={provider.id}>
                        {provider.name}
                      </option>
                    ))}
                  </select>
                  <ChevronDown aria-hidden="true" className={classes.selectIcon} size={15} />
                </div>
                {errors.user_id ? (
                  <span className={classes.errorText}>{errors.user_id}</span>
                ) : null}
              </label>

              <label className={classes.field}>
                <span className={classes.label}>
                  Status <span className={classes.required}>*</span>
                </span>
                <div className={classes.selectWrap}>
                  <select
                    className={`${classes.select} ${errors.status ? classes.inputError : ""}`}
                    onChange={(e) => setField("status", e.target.value)}
                    value={form.status}
                  >
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </select>
                  <ChevronDown aria-hidden="true" className={classes.selectIcon} size={15} />
                </div>
                {errors.status ? (
                  <span className={classes.errorText}>{errors.status}</span>
                ) : null}
              </label>
            </div>

            {serverError ? <div className={classes.serverError}>{serverError}</div> : null}

            {!isEdit && nameDobMatches.length > 0 ? (
              <div className={classes.warningBox}>
                <p className={classes.warningTitle}>Possible matching client found</p>
                <ul className={classes.warningList}>
                  {nameDobMatches.map((match) => (
                    <li key={match.id}>
                      <button
                        className={classes.warningLink}
                        onClick={() => openExistingClient(match.id)}
                        type="button"
                      >
                        {[match.firstName, match.lastName].filter(Boolean).join(" ")} (
                        {match.clientid})
                      </button>
                    </li>
                  ))}
                </ul>
                <button
                  className={classes.continueButton}
                  disabled={submitLoading}
                  onClick={() => submitClient({ acknowledgeNameDobWarning: true })}
                  type="button"
                >
                  Continue anyway
                </button>
              </div>
            ) : null}
          </div>

          <div className={classes.footer}>
            <button className={classes.cancelButton} onClick={requestClose} type="button">
              Cancel
            </button>
            <button className={classes.submitButton} disabled={submitLoading} type="submit">
              {submitLoading
                ? isEdit
                  ? "Saving..."
                  : "Adding..."
                : isEdit
                  ? "Save"
                  : "Add Client"}
            </button>
          </div>
        </form>
      </aside>

      {confirmDiscard ? (
        <div className={classes.confirmRoot} role="alertdialog">
          <div className={classes.confirmCard}>
            <h3 className={classes.confirmTitle}>Discard unsaved changes?</h3>
            <p className={classes.confirmText}>
              You have entered client information that has not been saved.
            </p>
            <div className={classes.confirmActions}>
              <button
                className={classes.cancelButton}
                onClick={() => setConfirmDiscard(false)}
                type="button"
              >
                Keep editing
              </button>
              <button className={classes.submitButton} onClick={confirmClose} type="button">
                Discard
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {dobOpen && dobMenuStyle
        ? createPortal(
            <div
              className={classes.calendarDropdown}
              id="client-form-dob-calendar"
              style={dobMenuStyle}
            >
              <DatePicker
                calendarClassName={classes.calendar}
                dropdownMode="select"
                inline
                maxDate={new Date()}
                onChange={(date) => {
                  setField("dob", formatDateToDisplay(date));
                  setDobOpen(false);
                }}
                selected={parseDisplayDob(form.dob)}
                showMonthDropdown
                showYearDropdown
              />
            </div>,
            document.body
          )
        : null}
    </div>,
    document.body
  );
};

export default ClientFormDrawer;
